import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { UserPlus, MoreHorizontal } from "lucide-react";
import Pagination from "@/components/pagination/Pagination";
import { useState } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import FormAccountModification from "@/components/dashboard/FormAccountModification";
import FormNewAccount from "@/components/dashboard/FormNewAccount";
import { TUsersList } from "@/api/DashboardTypes";
import axios from "axios";
import { toast } from "react-toastify";

interface TableAccountsListProps {
  AccountsList: TUsersList[];
}

const TableAccountsList: React.FC<TableAccountsListProps> = ({
  AccountsList,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };
  const AccountsPerPage = 7;
  const totalPages = Math.ceil(AccountsList.length / AccountsPerPage);
  const indexOfLastData = currentPage * AccountsPerPage;
  const indexOfFirstData = indexOfLastData - AccountsPerPage;

  // Sort accounts list by last name alphabetically
  const sortedAccountsList = [...AccountsList].sort((a, b) =>
    a.lastName.localeCompare(b.lastName)
  );

  const currentAccounts = sortedAccountsList.slice(
    indexOfFirstData,
    indexOfLastData
  );

  const DeleteUser = (id: number, email: string) => {
    axios
      .delete(`/dashboard/user/` + id)
      .then(() => {
        toast(`Le compte avec l'email : ${email} a bien été supprimé`, {
          position: "top-center",
          autoClose: 2000,
        });
      })
      .catch((error) => {
        console.error("Error response:", error.response);
        console.error("Erreur lors de la suppression de l'utilisateur:", error);
        toast.error(
          `Erreur lors de la suppression du compte avec l'email : ${email}`,
          {
            position: "top-center",
            autoClose: 2000,
          }
        );
      });
  };

  return (
    <Card className="w-5/6 h-5/6">
      <CardHeader className="flex flex-row items-center">
        <div className="w-1/4 flex justify-center align-middle">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline">
                <UserPlus className="mr-2" /> Nouveau Compte
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl ml-24">
              <DialogHeader>
                <DialogTitle>Modifier le compte</DialogTitle>
                <FormNewAccount />
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </div>
        <div className="mx-auto">
          <CardTitle>Comptes IRON</CardTitle>
          <CardDescription>Liste des comptes créés</CardDescription>
        </div>
        <div className="w-1/4"></div>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Prénom</TableHead>
              <TableHead>Tag</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Date de création</TableHead>
              <TableHead>Etablissement</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody className="text-left">
            {currentAccounts.map((account: TUsersList) => (
              <TableRow
                key={account.id}
                className="cursor-pointer hover:bg-gray-100"
              >
                <TableCell>{account.lastName}</TableCell>
                <TableCell>{account.firstName}</TableCell>
                <TableCell>{account.role}</TableCell>
                <TableCell>{account.accountStatus}</TableCell>
                <TableCell>
                  {new Date(account.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>{account.clinic}</TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className="h-8 w-8 p-0">
                        <span className="sr-only">Open menu</span>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <Dialog>
                        <DialogTrigger>
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                          >
                            Modifier
                          </DropdownMenuItem>
                        </DialogTrigger>
                        <DialogContent className="max-w-4xl">
                          <DialogHeader>
                            <DialogTitle>Modifier le compte</DialogTitle>
                            <FormAccountModification accountData={account} />
                          </DialogHeader>
                        </DialogContent>
                      </Dialog>
                      <DropdownMenuSeparator />
                      <Dialog>
                        <DialogTrigger>
                          <DropdownMenuItem
                            onSelect={(e) => e.preventDefault()}
                          >
                            Effacer de la base de données
                          </DropdownMenuItem>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle> Êtes-vous absolument sûr ? </DialogTitle>
                            <DialogDescription>
                              Cette action ne peut pas être annulée. Cela
                              supprimera définitivement le compte ainsi les
                              patients associés et effacera les données de nos
                              serveurs.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="flex justify-center">
                            <Button
                              variant={"default"}
                              className="w-[240px]"
                              onClick={() =>
                                DeleteUser(account.id, account.email)
                              }
                            >
                              Confirmer
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <CardFooter className="flex justify-center">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </CardFooter>
    </Card>
  );
};

export default TableAccountsList;
